import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSuperAdminAuth } from '../../../contexts/SuperAdminAuthContext';
import { usePendingApprovals } from '../../../contexts/PendingApprovalsContext';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import {
  CheckCircle, XCircle, Clock, RefreshCw, Bot, User, Calendar, FileText, Eye,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Progress } from '../../../components/ui/progress';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { cn } from '../../../lib/utils';
import { PageHeader, MetricRow, FilterBar, DataTable } from '../design';

const AGENT_TONE = {
  sales: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  support: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  accounts: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  hr: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  inventory: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  collections: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
};
const agentTone = (t) => AGENT_TONE[t?.toLowerCase()] || 'bg-slate-500/10 text-slate-400 border-slate-500/20';

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

function ConfidenceBadge({ confidence }) {
  if (confidence == null) return <span className="gov-text-tertiary text-xs">—</span>;
  const pct = Math.round(confidence * 100);
  const color = pct < 60 ? 'bg-rose-500/10 text-rose-400' : pct < 80 ? 'bg-amber-500/10 text-amber-400' : 'bg-emerald-500/10 text-emerald-400';
  return <Badge variant="outline" className={cn('text-xs', color)}>{pct}% confidence</Badge>;
}

export default function Approvals() {
  const { isAuthenticated } = useSuperAdminAuth();
  const { decrementCount, refreshCount } = usePendingApprovals();
  const [loading, setLoading] = useState(true);
  const [approvals, setApprovals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [agentFilter, setAgentFilter] = useState('all');
  const [reviewDialog, setReviewDialog] = useState({ open: false, approval: null, action: '' });
  const [reviewNotes, setReviewNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [detailDialog, setDetailDialog] = useState({ open: false, approval: null });

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await governanceAPI.getPendingApprovals(100);
      setApprovals(res.data.approvals || []);
    } catch (error) {
      setApprovals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAuthenticated) fetchApprovals(); }, [isAuthenticated, fetchApprovals]);

  const filteredApprovals = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return approvals.filter((a) => {
      const matchesSearch = !q ||
        a.agent_type?.toLowerCase().includes(q) || a.entity_type?.toLowerCase().includes(q) ||
        a.entity_id?.toLowerCase().includes(q) || a.requested_by?.toLowerCase().includes(q);
      const matchesAgent = agentFilter === 'all' || a.agent_type === agentFilter;
      return matchesSearch && matchesAgent;
    });
  }, [approvals, searchQuery, agentFilter]);

  const agentOptions = useMemo(() => {
    const types = new Set(approvals.map((a) => a.agent_type).filter(Boolean));
    return Array.from(types).map((t) => ({ value: t, label: `${t.charAt(0).toUpperCase() + t.slice(1)} Agent` }));
  }, [approvals]);

  const openReviewDialog = (approval, action) => { setReviewDialog({ open: true, approval, action }); setReviewNotes(''); };
  const openDetailDialog = (approval) => setDetailDialog({ open: true, approval });

  const handleReview = async () => {
    if (reviewDialog.action === 'reject' && !reviewNotes.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setProcessing(true);
    try {
      const approvalId = reviewDialog.approval?.id;
      const payload = { reason: reviewNotes || undefined };
      if (reviewDialog.action === 'approve') {
        await governanceAPI.approveRequest(approvalId, payload);
        toast.success('AI request approved successfully');
      } else {
        await governanceAPI.rejectRequest(approvalId, payload);
        toast.success('AI request rejected');
      }
      decrementCount();
      setReviewDialog({ open: false, approval: null, action: '' });
      fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process review');
      refreshCount();
    } finally {
      setProcessing(false);
    }
  };

  const metrics = [
    { key: 'pending-count', label: 'Pending', value: approvals.filter((a) => a.status === 'pending').length, icon: Clock, tone: 'warning' },
    { key: 'approved-count', label: 'Approved', value: approvals.filter((a) => a.status === 'approved').length, icon: CheckCircle, tone: 'success' },
    { key: 'rejected-count', label: 'Rejected', value: approvals.filter((a) => a.status === 'rejected').length, icon: XCircle, tone: 'danger' },
  ];

  const columns = [
    { accessorKey: 'agent_type', header: 'Agent / Action', cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <Bot className="h-4 w-4 gov-text-tertiary" />
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className={cn('text-xs capitalize', agentTone(row.original.agent_type))}>{row.original.agent_type || 'Unknown'} Agent</Badge>
          {row.original.execution_mode && (
            <Badge variant="outline" className={cn('text-xs', row.original.execution_mode === 'LIVE' ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' : 'bg-slate-500/10 text-slate-400 border-slate-500/20')}>{row.original.execution_mode}</Badge>
          )}
        </div>
      </div>
    ) },
    { accessorKey: 'entity_type', header: 'Entity', cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="font-medium gov-text-primary">{row.original.entity_type || 'Entity'}</span>
        <span className="text-xs gov-text-tertiary">ID: {row.original.entity_id?.slice(0, 8)}…</span>
      </div>
    ) },
    { accessorKey: 'requested_by', header: 'Requester', cell: ({ row }) => (
      <span className="flex items-center gap-2 text-sm gov-text-secondary"><User className="h-4 w-4 gov-text-tertiary" />{row.original.requested_by || 'System'}</span>
    ) },
    { accessorKey: 'confidence_score', header: 'Confidence', cell: ({ row }) => <ConfidenceBadge confidence={row.original.confidence_score} /> },
    { accessorKey: 'created_at', header: 'Requested', cell: ({ row }) => (
      <span className="flex items-center gap-2 text-sm gov-text-tertiary"><Calendar className="h-4 w-4" />{formatDate(row.original.created_at)}</span>
    ) },
    { id: 'actions', header: '', cell: ({ row }) => (
      <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
        <Button size="sm" variant="ghost" onClick={() => openDetailDialog(row.original)} data-testid={`view-details-${row.original.id}`}><Eye className="h-4 w-4" /></Button>
        <Button size="sm" variant="outline" className="text-emerald-500 hover:text-emerald-600" onClick={() => openReviewDialog(row.original, 'approve')} data-testid={`approve-${row.original.id}`}><CheckCircle className="h-4 w-4 mr-1" />Approve</Button>
        <Button size="sm" variant="outline" className="text-rose-500 hover:text-rose-600" onClick={() => openReviewDialog(row.original, 'reject')} data-testid={`reject-${row.original.id}`}><XCircle className="h-4 w-4 mr-1" />Reject</Button>
      </div>
    ) },
  ];

  return (
    <div className="space-y-8" data-testid="approvals-page">
      <PageHeader
        title="AI Approval Requests"
        subtitle="Review and approve pending AI actions requiring human oversight"
        actions={
          <Button variant="outline" size="sm" onClick={fetchApprovals} data-testid="refresh-approvals">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
        }
      />

      <MetricRow metrics={metrics} cols={3} />

      <FilterBar
        search={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search by agent, entity, or requester…' }}
        filters={[{ key: 'agent', label: 'Agents', value: agentFilter, onChange: setAgentFilter, options: agentOptions, defaultLabel: 'All Agents' }]}
        resultCount={filteredApprovals.length}
        onReset={() => { setSearchQuery(''); setAgentFilter('all'); }}
      />

      <DataTable
        columns={columns}
        data={filteredApprovals}
        loading={loading}
        testId="approvals-table"
        onRowClick={(a) => openDetailDialog(a)}
        emptyIcon={CheckCircle}
        emptyTitle="All caught up!"
        emptyHint="No pending AI approval requests at this time. New requests appear here when AI actions require human oversight."
        pageSize={25}
      />

      {/* Review Dialog */}
      <Dialog open={reviewDialog.open} onOpenChange={(open) => !open && setReviewDialog({ open: false, approval: null, action: '' })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {reviewDialog.action === 'approve'
                ? <><CheckCircle className="h-5 w-5 text-emerald-500" />Approve AI Request</>
                : <><XCircle className="h-5 w-5 text-rose-500" />Reject AI Request</>}
            </DialogTitle>
            <DialogDescription>
              {reviewDialog.action === 'approve'
                ? 'This will allow the AI agent to proceed with the requested action.'
                : 'This will prevent the AI agent from executing the requested action.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2 mb-2"><Bot className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{reviewDialog.approval?.agent_type} Agent</span></div>
              <p className="text-sm text-muted-foreground">Action on {reviewDialog.approval?.entity_type} #{reviewDialog.approval?.entity_id?.slice(0, 8)}</p>
              {reviewDialog.approval?.entity_summary && <p className="text-sm mt-2">{reviewDialog.approval.entity_summary}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="review-notes">{reviewDialog.action === 'reject' ? 'Rejection Reason (required)' : 'Notes (optional)'}</Label>
              <Textarea id="review-notes" placeholder={reviewDialog.action === 'reject' ? 'Please explain why this request is being rejected…' : 'Add any notes about this approval…'} value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} className="min-h-[100px]" data-testid="review-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialog({ open: false, approval: null, action: '' })}>Cancel</Button>
            <Button onClick={handleReview} disabled={processing || (reviewDialog.action === 'reject' && !reviewNotes.trim())} variant={reviewDialog.action === 'approve' ? 'default' : 'destructive'} data-testid="confirm-review">
              {processing ? 'Processing…' : reviewDialog.action === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => !open && setDetailDialog({ open: false, approval: null })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-amber-500" />Request Details</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Status</span>
              <Badge variant="outline" className={cn(
                detailDialog.approval?.status === 'pending' && 'bg-amber-500/10 text-amber-500',
                detailDialog.approval?.status === 'approved' && 'bg-emerald-500/10 text-emerald-500',
                detailDialog.approval?.status === 'rejected' && 'bg-rose-500/10 text-rose-500')}>
                {detailDialog.approval?.status?.toUpperCase() || 'PENDING'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Agent Type</span>
              <Badge variant="outline" className={agentTone(detailDialog.approval?.agent_type)}>{detailDialog.approval?.agent_type || 'Unknown'} Agent</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Entity</span>
              <span className="font-medium">{detailDialog.approval?.entity_type} #{detailDialog.approval?.entity_id?.slice(0, 8)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Execution Mode</span>
              <Badge variant="outline" className={cn(detailDialog.approval?.execution_mode === 'LIVE' ? 'bg-rose-500/10 text-rose-500' : 'bg-slate-500/10 text-slate-500')}>{detailDialog.approval?.execution_mode || 'SIMULATED'}</Badge>
            </div>
            {detailDialog.approval?.confidence_score != null && (
              <div className="space-y-2">
                <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Confidence Score</span><span className="font-medium">{Math.round(detailDialog.approval.confidence_score * 100)}%</span></div>
                <Progress value={detailDialog.approval.confidence_score * 100} className="h-2" />
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Requested By</span>
              <span className="flex items-center gap-2"><User className="h-4 w-4 text-muted-foreground" />{detailDialog.approval?.requested_by || 'System'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Created</span>
              <span>{formatDate(detailDialog.approval?.created_at)}</span>
            </div>
            {detailDialog.approval?.request_reason && (
              <div className="space-y-2 pt-2 border-t"><span className="text-sm text-muted-foreground">Request Reason</span><p className="text-sm bg-muted/50 p-3 rounded-lg">{detailDialog.approval.request_reason}</p></div>
            )}
            {detailDialog.approval?.entity_summary && (
              <div className="space-y-2"><span className="text-sm text-muted-foreground">Entity Summary</span><p className="text-sm bg-muted/50 p-3 rounded-lg">{detailDialog.approval.entity_summary}</p></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialog({ open: false, approval: null })}>Close</Button>
            {detailDialog.approval?.status === 'pending' && (
              <>
                <Button variant="outline" className="text-emerald-600" onClick={() => { setDetailDialog({ open: false, approval: null }); openReviewDialog(detailDialog.approval, 'approve'); }}><CheckCircle className="h-4 w-4 mr-1" />Approve</Button>
                <Button variant="destructive" onClick={() => { setDetailDialog({ open: false, approval: null }); openReviewDialog(detailDialog.approval, 'reject'); }}><XCircle className="h-4 w-4 mr-1" />Reject</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
